import { isAxiosError } from 'axios';
import { capitalize, ref } from 'vue';
import { toast } from 'vue3-toastify'

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export function formatEventDateRange(start: string, end?: string | null): string {
  if (!start) return '';
  const d1 = new Date(start + 'T12:00:00');
  if (isNaN(d1.getTime())) return start;
  const day1 = d1.getDate();
  const month1 = MONTH_NAMES[d1.getMonth()];
  const year1 = d1.getFullYear();
  if (!end || end === start) return `${day1} ${month1} ${year1}`;
  const d2 = new Date(end + 'T12:00:00');
  if (isNaN(d2.getTime())) return `${day1} ${month1} ${year1}`;
  const day2 = d2.getDate();
  const month2 = MONTH_NAMES[d2.getMonth()];
  const year2 = d2.getFullYear();
  if (month1 === month2 && year1 === year2) return `${day1}-${day2} ${month1} ${year1}`;
  return `${day1} ${month1} - ${day2} ${month2} ${year2}`;
}

const error = ref('');

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date:', dateStr);
    return '-';
  }
  let formatted = date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  // Capitalize only the first letter of the month
  const parts = formatted.split(' ');
  if (parts.length === 3) {
    parts[1] = parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
    formatted = parts.join(' ');
  }
  return formatted;
}

export function formatBirthdate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date:', dateStr);
    return '-';
  }
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatTelephone(telStr: string | null | undefined): string {
  if (!telStr) return '-';
  const cleaned = telStr.replace(/\D/g, ''); // Remove non-digit characters if any
  if (cleaned.length === 0 || isNaN(Number(cleaned))) {
    console.warn('Invalid telephone:', telStr);
    return '-';
  }
  let formatted = '';
  for (let i = 0; i < cleaned.length; i += 2) {
    if (i > 0) formatted += ' ';
    formatted += cleaned.slice(i, i + 2);
  }
  return formatted;
}

export const generateLicenseIdentifier = (license: License): string => {
  // Gestion des valeurs nulles ou indéfinies
  if (!license.name || !license.firstname || !license.birth_date) {
    console.warn('Invalid input for identifier:', {
      name: license.name,
      firstname: license.firstname,
      birth_date: license.birth_date,
    });
    return '';
  }

  // Nettoyer le nom et le prénom
  const cleanedName = license.name.trim().toUpperCase().replace(/\s+/g, '');
  const cleanedFirstname = capitalize(license.firstname.trim()).replace(/\s+/g, '');

  // Convertir la date en nombre Excel
  const date = new Date(license.birth_date);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date:', license.birth_date);
    return '';
  }
  const excelEpoch = new Date(1899, 11, 31); // 31/12/1899
  const diffInMs = date.getTime() - excelEpoch.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const excelDateNumber = String(diffInDays + 2); // +2 pour ajuster l'écart

  // Concaténer pour former l'identifiant
  return `${cleanedName}${cleanedFirstname}${excelDateNumber}`;
};

export const fullName = (user: any) => {
  const name = user.name ? user.name.trim().toUpperCase() : '';
  const firstname = user.firstname ? capitalize(user.firstname.trim()) : '';
  const full = `${name} ${firstname}`.trim();
  return full ? full : '';
};

export const handleError = (err: any, context: string) => {
  console.error(`Error while ${context}`, err);

  if (isAxiosError(err)) {
    if (err.response) {
      // Vérification du statut HTTP
      if (err.response?.status === 403) {
        error.value = '⛔ Access denied: administrators or editors only.';
      } else if (err.response.status === 401) {
        error.value = '🔐 Session expired. Please log in again.';
      } else {
        // Accès au message d'erreur renvoyé par l'API
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'An unknown error occurred';
        error.value = `An error occurred while ${context}: ${errorMessage}`;
      }
    } else if (err.request) {
      // Si aucune réponse n'a été reçue (erreur réseau par exemple)
      error.value = `Network error while ${context}. Please check your connection.`;
    } else {
      // Si l'erreur est interne à Axios (par exemple une erreur de configuration)
      error.value = `Error while ${context}: ${err.message}`;
    }

    // Affichage du toast avec l'erreur
    toast.error(error.value);
  } else {
    // Si l'erreur n'est pas une erreur Axios, affiche un message générique
    error.value = `An error occurred while ${context}: ${err.message || 'Unknown error'}`;
    toast.error(error.value);
  }
};

// Prénom : minuscules, majuscule après le début, un espace ou un tiret (accents
// compris : "élise" -> "Élise", "jean-pierre" -> "Jean-Pierre"). formatFirstname ne
// coupe PAS les espaces, pour rester utilisable à chaque frappe (sinon impossible de
// taper "Jean Pierre" : l'espace final serait retiré aussitôt) ; normalizeFirstname
// est la version finale, à l'envoi.
export const formatFirstname = (str: string): string =>
  str.toLowerCase().replace(/(?:^|[\s-])(\p{L})/gu, c => c.toUpperCase());

export const normalizeFirstname = (str: string): string => formatFirstname(str.trim());

export function titleCase(str: string): string {
  if (!str) return '';
  const words = str.toLowerCase().split(' ');
  return words.map(word => {
    const subwords = word.split('-');
    return subwords.map(sub => sub.charAt(0).toUpperCase() + sub.slice(1)).join('-');
  }).join(' ');
}

export const normalize = (str: string) => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-zA-Z\s-]/g, '')
    .toLowerCase()
    .trim();
};

// Libellés officiels FFD des catégories de classement (actés le 2026-10-19) : plus
// d'individuel mixte -- le code `individuel_mixte` est conservé en base mais désigne les
// masculins -- et le "double mixte" s'appelle simplement "Double" (ouvert à toutes les
// compositions). Les clés sans préfixe sont celles du classement national FFD.
export const RANKING_CATEGORY_LABELS: Record<string, string> = {
  individuel_mixte: 'Individuel Masculin',
  individuel_feminin: 'Individuel Féminin',
  individuel_veteran: 'Individuel Vétéran',
  individuel_junior: 'Individuel Junior',
  double_mixte: 'Double',
  double_feminin: 'Double Féminin',
  masculin: 'Individuel Masculin',
  feminin: 'Individuel Féminin',
  veteran: 'Individuel Vétéran',
  junior: 'Individuel Junior',
};

export const formatCategory = (cat: string) => {
  return RANKING_CATEGORY_LABELS[cat]
    ?? cat.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// Règle d'affichage d'une doublette (actée le 2026-09-30, sur tous les sites, classements
// et listes d'inscrits) : les deux joueurs par ordre alphabétique du nom, puis du prénom
// si le nom est identique. Miroir de leaderboards/functions.py::sorted_pair côté backend.
interface PlayerNameLike {
  name?: string | null
  firstname?: string | null
}

const nameCollator = new Intl.Collator('fr', { sensitivity: 'base' });

export function comparePlayerNames(a: PlayerNameLike, b: PlayerNameLike): number {
  return nameCollator.compare((a.name ?? '').trim(), (b.name ?? '').trim())
    || nameCollator.compare((a.firstname ?? '').trim(), (b.firstname ?? '').trim());
}

export function orderPair<T extends PlayerNameLike>(a: T, b: T): [T, T] {
  return comparePlayerNames(a, b) <= 0 ? [a, b] : [b, a];
}

// Miroir JS de Dartshub/backend/modules/api/licenses/routes.py::_calculate_category --
// même règle FFD (référence au 31 décembre de la 1ère année de saison, "V" dès 50 ans
// quel que soit le genre) utilisée côté client pour griser en amont les catégories
// individuelles (M/V/F/J) non éligibles, avant même l'appel serveur qui reste la
// vérité (cf. events/routes.py::_validate_category_eligibility). Ne pas dupliquer le
// seuil ailleurs : VETERAN_AGE doit rester identique aux deux endroits.
export const VETERAN_AGE = 50;

export function juniorCutoffDate(season: string): Date {
  const startYear = parseInt(season.split('-')[0], 10);
  return new Date(startYear, 11, 31); // 31 décembre de la 1ère année de la saison
}

// Retourne la catégorie FFD (M/F/JG/JF/V) pour un genre + date de naissance donnés,
// évaluée au 31/12 de la saison -- '' si genre ou date de naissance manquants.
export function calculateFfdCategory(
  gender: string | null | undefined,
  birthDate: string | Date | null | undefined,
  season: string,
): string {
  if (!gender || !birthDate) return '';
  const birth = typeof birthDate === 'string' ? new Date(birthDate + 'T00:00:00') : birthDate;
  if (isNaN(birth.getTime())) return '';
  const ref = juniorCutoffDate(season);
  let age = ref.getFullYear() - birth.getFullYear();
  const refMD = ref.getMonth() * 100 + ref.getDate();
  const birthMD = birth.getMonth() * 100 + birth.getDate();
  if (refMD <= birthMD) age -= 1;
  const isMinor = age < 18;
  const isVeteran = age >= VETERAN_AGE;
  const g = gender.toLowerCase().trim();
  if (['féminin', 'feminin', 'f', 'femme'].includes(g)) {
    if (isVeteran) return 'V';
    return isMinor ? 'JF' : 'F';
  }
  if (['masculin', 'm', 'homme'].includes(g)) {
    if (isVeteran) return 'V';
    return isMinor ? 'JG' : 'M';
  }
  return '';
}

export function isFemaleGender(gender: string | null | undefined): boolean {
  return !!gender && ['féminin', 'feminin', 'f', 'femme'].includes(gender.toLowerCase().trim());
}

// N'importe quelle valeur déclarée qui n'est pas "féminin" est traitée comme masculine
// -- les <select> du formulaire n'offrent que ces deux options, pas de 3e valeur à gérer.
export function isMaleGender(gender: string | null | undefined): boolean {
  return !!gender && !isFemaleGender(gender);
}

// Miroir JS de events/routes.py::_eligible_simple_categories -- catégories
// individuelles (M/V/F/J) ouvertes à un joueur selon sa catégorie FFD (M/F/JG/JF/V,
// licence si trouvée sinon calculée via calculateFfdCategory) et son genre. Masculin
// est fermé à toute joueuse (fin de l'individuel mixte FFD, actée le 2026-10-19) ;
// Vétéran débloque Vétéran ; Junior débloque Junior ; Féminin est ouvert à toute
// joueuse (adulte, junior ou vétérane), jamais à un homme.
export function eligibleSimpleCategories(
  ffdCategory: string | null | undefined,
  gender: string | null | undefined,
): Set<string> {
  const female = isFemaleGender(gender) || ffdCategory === 'F' || ffdCategory === 'JF';
  const allowed = new Set([female ? 'F' : 'M']);
  if (ffdCategory === 'JG' || ffdCategory === 'JF') allowed.add('J');
  if (ffdCategory === 'V') allowed.add('V');
  return allowed;
}

// Miroir JS de events/routes.py::_simple_cumul_exception_pair -- seule exception à la
// règle "une catégorie individuelle à la fois" : un junior peut cumuler Junior avec une
// 2e catégorie qui dépend de son genre (Masculin pour un garçon, Féminin pour une
// fille). Aucune exception pour un vétéran, même éligible à V/F.
export function simpleCumulExceptionPair(ffdCategory: string | null | undefined): Set<string> | null {
  if (ffdCategory === 'JG') return new Set(['J', 'M']);
  if (ffdCategory === 'JF') return new Set(['J', 'F']);
  return null;
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  return a.size === b.size && [...a].every(v => b.has(v));
}

// Une catégorie individuelle supplémentaire n'est cumulable avec celles déjà
// sélectionnées que si le résultat correspond exactement à l'exception junior de
// `ffdCategory` -- miroir de la règle appliquée côté serveur dans
// _validate_category_eligibility.
export function wouldViolateSimpleCumul(
  current: string[],
  candidate: string,
  ffdCategory: string | null | undefined,
): boolean {
  if (current.includes(candidate)) return false; // désélection : toujours permise
  const next = new Set([...current, candidate]);
  if (next.size <= 1) return false;
  const pair = simpleCumulExceptionPair(ffdCategory);
  return !(pair && setsEqual(next, pair));
}

// Miroir JS de la vérification "DF" dans events/routes.py::_validate_category_eligibility
// -- Double Féminine réservée aux femmes, joueur ET partenaire. Ne grise "DF" que si un
// genre masculin est explicitement connu pour l'un des deux ; un genre encore inconnu
// (partenaire pas encore saisi) ne bloque pas la sélection, la vérité reste le serveur.
export function ineligibleDoubleCategories(
  playerGender: string | null | undefined,
  partnerGender: string | null | undefined,
): string[] {
  return (isMaleGender(playerGender) || isMaleGender(partnerGender)) ? ['DF'] : [];
}

interface ExistingInscriptionLike {
  name: string
  firstname: string
  category_simple?: string | null
  category_double?: string | null
}

// Catégories (simple, double) qu'un joueur porte déjà sur une éventuelle ligne
// d'inscription existante pour cet événement -- sert à masquer côté client les
// catégories déjà prises (proposer de les reprendre n'aurait aucun effet, cf.
// events/routes.py::_create_inscription_rows, qui les ignore silencieusement).
export function existingCategoriesForPlayer(
  inscriptions: ExistingInscriptionLike[],
  name: string,
  firstname: string,
): { simple: Set<string>; double: Set<string> } {
  const simple = new Set<string>();
  const double = new Set<string>();
  const key = `${normalize(name)} ${normalize(firstname)}`;
  if (!normalize(name) || !normalize(firstname)) return { simple, double };
  for (const ins of inscriptions) {
    if (`${normalize(ins.name)} ${normalize(ins.firstname)}` !== key) continue;
    (ins.category_simple || '').split(',').map(c => c.trim()).filter(Boolean).forEach(c => simple.add(c));
    (ins.category_double || '').split(',').map(c => c.trim()).filter(Boolean).forEach(c => double.add(c));
  }
  return { simple, double };
}
